import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { LoadingSpinner, StatusBadge } from '../../components/UI';
import { format } from 'date-fns';

// ── Export helpers ───────────────────────────────────────────
const exportCSV = (data, totals, cols) => {
  const headers = ['NOC Type', ...cols.map(c => c.label)];
  const rows = data.map(row => [row.noc_name, ...cols.map(c => row[c.key] || 0)]);
  const totalRow = ['TOTAL', ...cols.map(c => totals[c.key] || 0)];
  const csv = [headers, ...rows, totalRow].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = `noc-report-${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

const printTable = () => {
  window.print();
};

// ── Status label/color map ────────────────────────────────────
const STATUS_META = {
  pending:   { label: 'Pending',   bg: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  approved:  { label: 'Approved',  bg: 'bg-green-100 text-green-700 border-green-300' },
  rejected:  { label: 'Rejected',  bg: 'bg-red-100 text-red-700 border-red-300' },
  objection: { label: 'Objection', bg: 'bg-orange-100 text-orange-700 border-orange-300' },
  forwarded: { label: 'Forwarded', bg: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  total:     { label: 'Total',     bg: 'bg-blue-100 text-blue-700 border-blue-300' },
};

// ── Drill-down Modal ──────────────────────────────────────────
function DrillDownModal({ filter, onClose }) {
  const [apps, setApps]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]     = useState(1);
  const navigate = useNavigate();

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (filter.noc_type_id) params.append('noc_type_id', filter.noc_type_id);
      if (filter.status_filter) params.append('status_filter', filter.status_filter);
      if (filter.date_from) params.append('date_from', filter.date_from);
      if (filter.date_to)   params.append('date_to', filter.date_to);
      const r = await api.get(`/reports/noc-detail?${params}`);
      setApps(r.applications || []);
      setTotal(r.total || 0);
      setPage(p);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(1); }, [load]);

  const totalPages = Math.ceil(total / 20);
  const meta = STATUS_META[filter.status_filter || 'total'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800">
              {filter.noc_name} — {meta?.label || 'All'}
            </h2>
            <p className="text-sm text-gray-500">{total} record(s) found</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl text-xl">✕</button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <LoadingSpinner size="lg" className="py-20" />
          ) : apps.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <p className="text-4xl mb-3">📂</p>
              <p>No records found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>App No</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>NOC Type</th>
                  <th>Status</th>
                  <th>Department</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app, i) => (
                  <tr key={app.id}>
                    <td className="text-xs text-gray-400">{(page-1)*20+i+1}</td>
                    <td className="font-mono text-xs text-blue-700 font-semibold">{app.application_no}</td>
                    <td className="text-sm">{app.applicant_name || app.user_name}</td>
                    <td className="text-xs text-gray-500">{app.applicant_mobile}</td>
                    <td className="text-sm font-medium">{app.noc_name}</td>
                    <td><StatusBadge status={app.status} /></td>
                    <td className="text-xs text-gray-500">{app.current_dept || '—'}</td>
                    <td className="text-xs text-gray-400">
                      {app.submitted_at ? format(new Date(app.submitted_at), 'dd/MM/yyyy') : '—'}
                    </td>
                    <td>
                      <Link to={`/admin/applications/${app.id}`}
                        onClick={onClose}
                        className="text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
            <button onClick={() => load(page-1)} disabled={page===1}
              className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">← Prev</button>
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => load(page+1)} disabled={page===totalPages}
              className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40">Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Report Component ─────────────────────────────────────
export default function NocReport() {
  const [data, setData]         = useState([]);
  const [totals, setTotals]     = useState({});
  const [nocTypes, setNocTypes] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ noc_type_id: '', date_from: '', date_to: '' });
  const [drillDown, setDrillDown] = useState(null);

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([,v]) => v)));
      const r = await api.get(`/reports/noc-summary?${params}`);
      setData(r.data || []);
      setTotals(r.totals || {});
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/noc-types/public').then(r => setNocTypes(r.noc_types || []));
    load();
  }, []);

  const openDrill = (row, statusFilter) => {
    setDrillDown({
      noc_type_id:   row?.noc_type_id || '',
      noc_name:      row?.noc_name || 'All NOC Types',
      status_filter: statusFilter,
      date_from:     filters.date_from,
      date_to:       filters.date_to,
    });
  };

  const COLS = [
    { key: 'total',     label: 'Total',     cls: 'text-blue-700 font-bold' },
    { key: 'pending',   label: 'Pending',   cls: 'text-yellow-700' },
    { key: 'approved',  label: 'Approved',  cls: 'text-green-700' },
    { key: 'forwarded', label: 'Forwarded', cls: 'text-indigo-700' },
    { key: 'rejected',  label: 'Rejected',  cls: 'text-red-700' },
    { key: 'objection', label: 'Objection', cls: 'text-orange-700' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📊 NOC Summary Report</h1>
          <p className="text-sm text-gray-500 mt-1">Click any number to drill down into details</p>
        </div>
        <div className="flex gap-2 no-print">
          <button onClick={printTable}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium transition-all">
            🖨️ Print
          </button>
          <button onClick={() => exportCSV(data, totals, COLS)}
            disabled={data.length === 0}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium transition-all disabled:opacity-40">
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && Object.keys(totals).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {COLS.map(col => (
            <button key={col.key}
              onClick={() => openDrill(null, col.key === 'total' ? '' : col.key)}
              className="card text-center hover:shadow-md transition-all cursor-pointer p-3">
              <p className={`text-2xl font-bold ${col.cls}`}>{totals[col.key] || 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">{col.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <select className="form-select text-sm" value={filters.noc_type_id}
            onChange={e => setFilters(f => ({ ...f, noc_type_id: e.target.value }))}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n => <option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>
          <input type="date" className="form-input text-sm" value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          <input type="date" className="form-input text-sm" value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          <div className="flex gap-2">
            <button onClick={() => load(filters)} className="btn-primary flex-1 text-sm">🔍 Apply</button>
            <button onClick={() => { const f={noc_type_id:'',date_from:'',date_to:''}; setFilters(f); load(f); }}
              className="btn-secondary text-sm px-3">✕</button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">📊</p>
          <p className="text-lg font-semibold text-gray-700">No data found</p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">NOC Type</th>
                  {COLS.map(col => (
                    <th key={col.key} className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={row.noc_type_id} className={`border-b hover:bg-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 font-semibold text-gray-800 text-sm">{row.noc_name}</td>
                    {COLS.map(col => (
                      <td key={col.key} className="text-center px-4 py-3">
                        {row[col.key] > 0 ? (
                          <button
                            onClick={() => openDrill(row, col.key === 'total' ? '' : col.key)}
                            className={`text-sm font-bold px-3 py-1 rounded-full border cursor-pointer hover:opacity-80 transition-all ${STATUS_META[col.key]?.bg || 'bg-gray-100 text-gray-700'}`}>
                            {row[col.key]}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-sm">0</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-blue-50 border-t-2 border-blue-200 font-bold">
                  <td className="px-4 py-3 text-sm text-blue-800">Total</td>
                  {COLS.map(col => (
                    <td key={col.key} className="text-center px-4 py-3">
                      <button
                        onClick={() => openDrill(null, col.key === 'total' ? '' : col.key)}
                        className={`text-sm font-bold px-3 py-1 rounded-full border ${STATUS_META[col.key]?.bg || ''}`}>
                        {totals[col.key] || 0}
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { font-size: 12px; }
          .card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
        }
      `}</style>

      {/* Drill Down Modal */}
      {drillDown && (
        <DrillDownModal filter={drillDown} onClose={() => setDrillDown(null)} />
      )}
    </div>
  );
}